import { supabase } from '../supabase';
import { withTimeout } from '../utils/async/withTimeout';
import { fetchAllRows } from '../utils/async/fetchAllRows';
import { bumpCreditsMutation } from '../utils/billing/creditsMutationSignal';
import { overRoleLimitMessageId } from '../utils/billing/customerCreditGrantCaps';

/**
 * Thin client wrapper around the customer-credit SECURITY DEFINER RPCs
 * (docs/superpowers/specs/2026-08-04-customer-credits-design.md, Phase 2b —
 * supabase/migrations/20260807130000_grant_customer_credit_rpc.sql,
 * 20260807140000_apply_release_customer_credit_rpc.sql (pre-tax math since
 * 20260807170000_apply_credit_pre_tax.sql), and
 * 20260808120000_manual_credit_removal.sql for remove/transfer). The DB is
 * the security + validation boundary — nothing here re-implements a cap, a
 * reason allowlist, or a role check; every function just forwards params and
 * maps the RAISEd token to an i18n key for the caller to format.
 *
 * `withTimeout` wraps every WRITE (grant/apply/release/remove/transfer), not
 * the ledger read — mirrors the documented iOS-PWA modal-freeze fix (memory:
 * modal-freeze-ios-rpc-timeout): mutation RPCs have no fetch timeout, so a
 * backgrounded PWA can hang a `saving` flag forever without it.
 */

// TOKEN CONTRACT — the exact strings the five RPCs RAISE (verified against
// the migration bodies). Keep in lockstep; a token missing here silently
// falls through to the caller's generic failure copy.
export const TOKEN_KEY_MAP = [
  ['CUSTOMER_NOT_FOUND', 'billing.credits.rpcError.customerNotFound'],
  ['INVOICE_NOT_FOUND', 'billing.credits.rpcError.invoiceNotFound'],
  ['MFA_REQUIRED', 'billing.credits.rpcError.mfaRequired'],
  ['NOT_AUTHORIZED', 'billing.credits.rpcError.notAuthorized'],
  ['INVALID_AMOUNT', 'billing.credits.rpcError.invalidAmount'],
  ['INVALID_REASON', 'billing.credits.rpcError.invalidReason'],
  ['NOTE_REQUIRED', 'billing.credits.rpcError.noteRequired'],
  ['NOTE_TOO_LONG', 'billing.credits.rpcError.noteTooLong'],
  ['OVER_ROLE_LIMIT', 'billing.credits.rpcError.overRoleLimit'],
  ['INVOICE_NOT_DRAFT', 'billing.credits.rpcError.invoiceNotDraft'],
  ['INVOICE_DELETED', 'billing.credits.rpcError.invoiceDeleted'],
  ['INVOICE_BUSY', 'billing.credits.rpcError.invoiceBusy'],
  // CUSTOMER_BUSY is INVOICE_BUSY's sibling for the customer-row lock taken by
  // remove/transfer (20260808140000 bounded both at 3s rather than letting the
  // request hang forever — an unbounded wait is the documented iOS-PWA
  // modal-freeze class). Same user-facing shape: transient, retry.
  ['CUSTOMER_BUSY', 'billing.credits.rpcError.customerBusy'],
  ['TENANT_MISMATCH', 'billing.credits.rpcError.tenantMismatch'],
  ['LEDGER_COLUMN_MISMATCH', 'billing.credits.rpcError.ledgerColumnMismatch'],
  ['INVALID_INVOICE_TOTAL', 'billing.credits.rpcError.invalidInvoiceTotal'],
  // Releasing credit restores invoices.total UP, which the estimate billing
  // cap (20260814200000) governs — a release racing a re-bill of the freed
  // slice aborts with this token. Precise copy beats the generic fallback.
  ['OVER_ESTIMATE_TOTAL', 'billing.credits.rpcError.overEstimateTotal'],
  // Manual removal/transfer (20260808120000) — admin-only.
  ['OVER_AVAILABLE', 'billing.credits.rpcError.overAvailable'],
  ['SAME_CUSTOMER', 'billing.credits.rpcError.sameCustomer'],
  ['TARGET_NOT_FOUND', 'billing.credits.rpcError.targetNotFound'],
  ['TARGET_ARCHIVED', 'billing.credits.rpcError.targetArchived'],
  // Pre-tax apply (20260807170000) — apply-only; actionable copy (the org
  // rate changed under a draft that already stored the OLD rate).
  ['TAX_RATE_MISMATCH', 'billing.credits.rpcError.taxRateMismatch'],
  // Pre-tax apply, second guard (20260808130000_credit_path_conflict_guard.sql)
  // — apply-only; fires when a SECOND apply on an already-credited invoice
  // would land on a different internal math path than the first (reachable
  // without tampering: apply most of a balance, the customer gets more
  // credit, apply again). Copy points at the recovery the UI already
  // exposes (Remove → Apply again) — never surfaces the token/internals.
  ['CREDIT_PATH_CONFLICT', 'billing.credits.rpcError.creditPathConflict'],
  // Two-bucket tax treatment (20260808150000_credit_tax_buckets.sql) —
  // remove/transfer's p_bucket is REQUIRED ('payment' | 'concession');
  // raised when it's missing or not one of those two values. Reachable only
  // if a future caller forgets to resolve/pass a bucket — the modals always
  // pass one, so this is a defensive mapping, not a normal user path.
  ['INVALID_BUCKET', 'billing.credits.rpcError.invalidBucket'],
  // Idempotency (RD-CX-023): same p_request_id re-sent with DIFFERENT args.
  ['IDEMPOTENCY_KEY_REUSED', 'billing.credits.rpcError.requestReused'],
];

/**
 * Map an RPC error (Supabase error object or raw message string) to an i18n
 * key id. Longer/more-specific tokens are checked before their substrings
 * would ever collide (none currently overlap, but order is preserved
 * deliberately — mirrors getRoleRpcErrorKey in employeeService.js).
 * Returns null when nothing matched, so the caller falls back to a generic
 * failure message — a raw SQL error must never reach the user.
 *
 * Optional `{ role }` picks admin vs billing OVER_ROLE_LIMIT copy — admins
 * should not be told to "ask an admin."
 */
export function getCustomerCreditRpcErrorKey(error, { role } = {}) {
  const code = typeof error === 'string' ? '' : (error?.code || '');
  const raw = typeof error === 'string' ? error : (error?.message || '');
  // Deadlock (40P01, 2026-09-28 credit-integrity) can reach the client from
  // any credit RPC that takes a row lock (apply/release/remove/transfer) —
  // Postgres's own deadlock message never contains one of our RAISE tokens,
  // so match the SQLSTATE / message directly. Same user-facing story as
  // INVOICE_BUSY (the RPCs' own explicit lock-busy RAISE): transient, retry.
  if (code === '40P01' || /deadlock detected/i.test(raw)) {
    return 'billing.credits.rpcError.invoiceBusy';
  }
  if (!raw) return null;
  for (const [token, key] of TOKEN_KEY_MAP) {
    if (!raw.includes(token)) continue;
    if (token === 'OVER_ROLE_LIMIT') return overRoleLimitMessageId(role);
    return key;
  }
  return null;
}

/**
 * grant_customer_credit(p_customer_id, p_amount, p_reason, p_note) → jsonb
 * {id, balance}. Tenant is resolved server-side from the customer row —
 * never pass tenantId here, the RPC would ignore it anyway (client-supplied
 * tenant is the textbook IDOR CLAUDE.md forbids).
 */
export async function grantCustomerCredit({ customerId, amount, reason, note, requestId }) {
  const { data, error } = await withTimeout(
    supabase.rpc('grant_customer_credit', {
      p_customer_id: customerId,
      p_amount: Number(amount),
      p_reason: reason,
      p_note: note && note.trim() ? note.trim() : null,
      ...(requestId ? { p_request_id: requestId } : {}),
    }),
  );
  // Refresh hint for the other credit surfaces (dashboard tile, draft-row
  // chips, sidebar available figure) — billing has no realtime, so nothing
  // else tells them this number moved. Only on a real success.
  if (!error && data) bumpCreditsMutation();
  return { data: data || null, error: error || null };
}

/**
 * apply_customer_credit_to_invoice(p_invoice_id, p_amount DEFAULT NULL) → jsonb.
 * Optional `amount` (20260811140000): positive 2-dp dollars to consume from
 * the pool; omit/null keeps historical "apply max" behavior. Server clamps
 * when the request exceeds balance/room.
 */
export async function applyCustomerCreditToInvoice(invoiceId, amount) {
  const params = { p_invoice_id: invoiceId };
  if (amount != null && amount !== '') {
    params.p_amount = Number(amount);
  }
  const { data, error } = await withTimeout(
    supabase.rpc('apply_customer_credit_to_invoice', params),
  );
  // Refresh hint for the other credit surfaces (dashboard tile, draft-row
  // chips, sidebar available figure) — billing has no realtime, so nothing
  // else tells them this number moved. A real (non-no_op) success ALSO
  // carries an `invoice_patch` detail (2026-08-12) built straight from the
  // RPC's own returned totals, so useCreditInvoicePatchSubscription can fold
  // it onto the matching dashboard row synchronously — the dashboards' own
  // in-memory `invoices` arrays are otherwise never re-fetched after this RPC
  // mutates invoices.total/tax_amount/taxable_base server-side. A no_op keeps
  // the prior bare-bump behavior: nothing moved, so no patch is owed.
  if (!error && data && !data.no_op) {
    // CUMULATIVE vs THIS-APPLY (security review, 2026-08-12): the RETURN's
    // applied_concession / applied_payment are THIS apply's legs only, while
    // the row's credit_applied_* columns are CUMULATIVE — they coincide only
    // when this apply started from zero (applied === total_applied), which
    // is every UI-reachable apply (the strip shows Remove while credited,
    // and the send-time appliers — 2026-08-13, which replaced the old
    // on-open offer — all pre-check for a zero-credit draft). For an API-level
    // stacked apply, OMIT the bucket keys rather than write deltas into
    // cumulative columns (these feed the customer-facing PDF via
    // buildInvoiceDocInputs) — applyCreditInvoicePatch skips absent keys, so
    // the row's buckets stay as they were, and the preview's on-open heal
    // corrects them from the live row next open. total / total_applied /
    // invoice_tax / invoice_taxable_base are the RPC's own post-write row
    // state — cumulative-true in every case.
    const fromZero = Number(data.applied) === Number(data.total_applied);
    bumpCreditsMutation({
      kind: 'invoice_patch',
      invoiceId,
      patch: {
        total: data.invoice_total,
        account_credit_applied: data.total_applied,
        ...(fromZero ? {
          credit_applied_concession: data.applied_concession,
          credit_applied_payment: data.applied_payment,
        } : {}),
        tax_amount: data.invoice_tax,
        taxable_base: data.invoice_taxable_base,
      },
    });
  } else if (!error && data) {
    bumpCreditsMutation();
  }
  return { data: data || null, error: error || null };
}

/**
 * release_customer_credit_from_invoice(p_invoice_id) → jsonb. Same Phase 4b
 * note as applyCustomerCreditToInvoice above.
 */
export async function releaseCustomerCreditFromInvoice(invoiceId) {
  const { data, error } = await withTimeout(
    supabase.rpc('release_customer_credit_from_invoice', { p_invoice_id: invoiceId }),
  );
  // Refresh hint for the other credit surfaces (dashboard tile, draft-row
  // chips, sidebar available figure) — billing has no realtime, so nothing
  // else tells them this number moved. A real (non-no_op) success ALSO
  // patches the matching dashboard row (2026-08-12), built STRAIGHT from the
  // RPC's own return: release is always FULL (the RPC takes no amount and
  // reverses the whole outstanding net), so all three credit columns are
  // zero afterwards, and the live definition (20260808150000's release body)
  // returns the restored invoice_total + invoice_tax + invoice_taxable_base.
  // Deliberately NO extra row read here — the sidebar's and the lock
  // notice's `releasing` flag block on this promise, so an unbounded read in
  // this seam would be the documented iOS-PWA stranded-flag class (code
  // review, 2026-08-12).
  if (!error && data && !data.no_op) {
    bumpCreditsMutation({
      kind: 'invoice_patch',
      invoiceId,
      patch: {
        total: data.invoice_total,
        account_credit_applied: 0,
        credit_applied_concession: 0,
        credit_applied_payment: 0,
        tax_amount: data.invoice_tax,
        taxable_base: data.invoice_taxable_base,
      },
    });
  } else if (!error && data) {
    bumpCreditsMutation();
  }
  return { data: data || null, error: error || null };
}

/**
 * remove_customer_credit(p_customer_id, p_amount, p_reason, p_note,
 * p_bucket[, p_request_id]) → jsonb {removed, customer_balance, bucket, bucket_balance}.
 * ADMIN-only server-side (20260808120000) — billing can grant but never take
 * credit away. reason ∈ 'recorded_in_error' | 'write_off'; a non-empty note
 * is always required. Tenant is resolved server-side from the customer row —
 * never pass it here.
 *
 * `bucket` ∈ 'payment' | 'concession' is REQUIRED since
 * 20260808150000_credit_tax_buckets.sql — the RPC's own OVER_AVAILABLE check
 * (and the removal ledger row) is scoped to that ONE pool, so a prepayment
 * balance can never absorb a goodwill write-off (which would move real
 * prepaid money into the concession pool and change the tax treatment of
 * everything applied afterwards). Omitting it raises INVALID_BUCKET — the
 * caller (RemoveCreditModal) always resolves one before calling this.
 */
export async function removeCustomerCredit({
  customerId, amount, reason, note, bucket, requestId,
}) {
  const { data, error } = await withTimeout(
    supabase.rpc('remove_customer_credit', {
      p_customer_id: customerId,
      p_amount: Number(amount),
      p_reason: reason,
      p_note: note && note.trim() ? note.trim() : null,
      p_bucket: bucket,
      ...(requestId ? { p_request_id: requestId } : {}),
    }),
  );
  // Refresh hint for the other credit surfaces (dashboard tile, draft-row
  // chips, sidebar available figure) — billing has no realtime, so nothing
  // else tells them this number moved. Only on a real success.
  if (!error && data) bumpCreditsMutation();
  return { data: data || null, error: error || null };
}

/**
 * transfer_customer_credit(p_from_customer_id, p_to_customer_id, p_amount,
 * p_note, p_bucket) → jsonb {transferred, from_balance, to_balance, bucket}.
 * ADMIN-only server-side (20260808120000). Writes a linked
 * transfer_out/transfer_in pair sharing one transfer_group_id; a non-empty
 * note is always required. Tenant is resolved server-side from the SOURCE
 * customer row.
 *
 * `bucket` ∈ 'payment' | 'concession' is REQUIRED since
 * 20260808150000_credit_tax_buckets.sql — the source's OVER_AVAILABLE check
 * is scoped to that bucket and BOTH linked legs are written in it (a
 * transfer moves money between customers, never between tax treatments).
 * Omitting it raises INVALID_BUCKET.
 */
export async function transferCustomerCredit({
  fromCustomerId, toCustomerId, amount, note, bucket, requestId,
}) {
  const { data, error } = await withTimeout(
    supabase.rpc('transfer_customer_credit', {
      p_from_customer_id: fromCustomerId,
      p_to_customer_id: toCustomerId,
      p_amount: Number(amount),
      p_note: note && note.trim() ? note.trim() : null,
      p_bucket: bucket,
      ...(requestId ? { p_request_id: requestId } : {}),
    }),
  );
  // Refresh hint for the other credit surfaces (dashboard tile, draft-row
  // chips, sidebar available figure) — billing has no realtime, so nothing
  // else tells them this number moved. Only on a real success.
  if (!error && data) bumpCreditsMutation();
  return { data: data || null, error: error || null };
}

/**
 * Raw, YEAR-UNBOUNDED read of the tenant's whole credit ledger — RLS
 * (cce_select_admin_billing) already restricts rows to admin/billing, this
 * adds the explicit tenant filter CLAUDE.md's tenant-scoping rule asks for.
 *
 * Deliberately NOT scoped to the dashboard's loaded year: a credit has no
 * year (a prior-year prepayment is live money today) — the exact bug class
 * documented in CLAUDE.md's "cross-time-window definition off the year-
 * scoped dashboard arrays" anti-pattern. Callers must fetch this
 * independently of `invoices`/`jobs`, never derive it from those arrays.
 *
 * `applied_invoice` is a PostgREST FK embed over `invoice_id` (the ONLY FK
 * from this table to `invoices`, so the relationship is unambiguous despite
 * both sides being tenant-composite FKs) — lets the History timeline render
 * "Applied to INV-2026-0222" instead of the generic "Applied to invoice"
 * (CustomerCreditHistoryItem.entryTitle). `invoice_number`/`invoice_number_year`
 * are covered by the same admin/billing SELECT RLS the rest of the app
 * already reads invoices under; a hard-deleted/inaccessible invoice embeds
 * as null and the caller falls back to the generic title.
 *
 * `status` rides the SAME embed (2026-08-17) so `onDraft` — "money committed
 * to a still-recoverable draft", computeCustomerCreditBalances' one input
 * beyond the ledger itself — no longer costs a SECOND round-trip. It used to
 * be a follow-up `invoices.select('id, status').in('id', ids)` inside
 * useCustomerCreditsData, which could only start once THIS query resolved
 * (it needs the ids), so the Credits segment held its loading skeleton for
 * two serial queries. The embed is strictly equivalent: same rows, same
 * admin/billing SELECT RLS, same null-on-inaccessible fallback — an invoice
 * the caller cannot read simply contributes no status, exactly as it
 * contributed no row to the old `.in()` result.
 */
export async function fetchCustomerCreditLedger(tenantId) {
  if (!tenantId) return { data: [], error: null };
  // No `page` param on this read at all — always go through fetchAllRows so
  // PostgREST's silent 1000-row cap cannot truncate a tenant's ledger.
  const { data, error } = await fetchAllRows((opts) =>
    supabase
      .from('customer_credit_entries')
      .select(`
        id, customer_id, entry_type, amount, reason, note, bucket, invoice_id, transfer_group_id, created_by, created_by_name, created_at,
        applied_invoice:invoices ( invoice_number, invoice_number_year, status )
      `, opts)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true }),
  );
  return { data: data || [], error: error || null };
}
